import { useState, useEffect } from 'react';
import { 
  Menu, X, ChevronDown, User, Briefcase, Trophy, 
  LogOut, Globe, ShieldCheck, Users, Settings, Activity, 
  GitBranch, BarChart3, Layout, Bell, Headphones,
  ArrowDownRight, ArrowUpRight, ArrowRightLeft, Package
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import NotificationDrawer from './notifications/NotificationDrawer';
import KYCBanner from './kyc/KYCBanner';
import { useBranding } from '../hooks/useBranding';


export default function Header() {
  const { t, i18n } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] = useState(false);
  
  const { user, logout, isAuthenticated } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotifications();
  const { branding } = useBranding();

  const navigate = useNavigate();
  const location = useLocation();

  const isAdmin = user?.role === 'admin';

  const publicLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.trading'), path: '/spot' },
    { name: t('nav.packs'), path: '/packs' },
    { name: t('nav.mlm'), path: '/mlm' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  const privateLinks = [
    { name: t('nav.dashboard'), path: '/dashboard' },
    { name: t('nav.spot'), path: '/spot' },
    { name: t('nav.futures'), path: '/futures' },
    { name: t('nav.packs'), path: '/packs' },
    { name: t('nav.bonuses'), path: '/bonuses' },
    { name: t('nav.team'), path: '/team' }
  ];

  const adminLinks = [
    { name: t('admin.dashboard'),       path: '/admin',                        icon: <Layout size={16} /> },
    { name: t('admin.users'),           path: '/admin/users',                  icon: <Users size={16} /> },
    { name: 'KYC',                      path: '/admin/kyc',                    icon: <ShieldCheck size={16} /> },
    { name: t('admin.invest_plans'),    path: '/admin/invest',                 icon: <BarChart3 size={16} /> },
    { name: t('admin.invest_types'),    path: '/admin/invest-types',           icon: <Briefcase size={16} /> },
    { name: t('admin.market_analyses'), path: '/admin/market-analyses',        icon: <Activity size={16} /> },
    { name: t('admin.mlm_settings'),    path: '/admin/mlm',                    icon: <GitBranch size={16} /> },
    { name: t('admin.transactions'),    path: '/admin/financial-transactions', icon: <Activity size={16} /> },
    { name: t('admin.settings'),        path: '/admin/settings',               icon: <Settings size={16} /> }
  ];


  const managerLinks = [
    { name: t('admin.manager_dashboard'), path: '/manager', icon: <Briefcase size={16} /> }
  ];

  const leaderLinks = [
    { name: t('admin.leader_dashboard'), path: '/leader', icon: <Trophy size={16} /> }
  ];

  const dropdownItems = [
    { name: t('nav.profile'), path: '/profile', icon: <User size={16} /> },
    { name: t('nav.settings'), path: '/settings', icon: <User size={16} /> },
    { name: t('nav.logout'), action: () => handleLogout(), icon: <LogOut size={16} /> }
  ];

  const handleLogout = () => {
    logout();
    setIsDropdownOpen(false);
    setIsMobileMenuOpen(false);
    navigate('/login');
  };

  const handleNotificationClick = (notif) => {
    setIsNotificationDrawerOpen(false);
    
    // Si c'est lié à un ticket de support
    if (notif.data && notif.data.ticket_id) {
      const ticketId = notif.data.ticket_id;
      if (isAdmin) {
        navigate(`/admin/support?id=${ticketId}`);
      } else {
        navigate(`/support?id=${ticketId}`);
      }
    }
    // Ajoutez d'autres navigations selon le type de notification ici
    else if (notif.type === 'deposit' || notif.type === 'withdrawal') {
       navigate('/dashboard');
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
      if (isLangDropdownOpen && !event.target.closest('.lang-dropdown-container')) {
        setIsLangDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen, isLangDropdownOpen]);

  const getUserDisplayName = () => {
    if (user?.username) return user.username;
    if (user?.email) return user.email.split('@')[0];
    return t('profile.user');
  };

  const getRoleSpecificLinks = () => {
    if (!user) return [];
    
    switch(user.role) {
      case 'admin':
        return adminLinks;
      case 'manager':
        return managerLinks;
      case 'leader':
        return leaderLinks;
      default:
        return [];
    }
  };

  const roleLinks = getRoleSpecificLinks();

  return (
    <header className={`glass-navbar sticky top-0 z-40 transition-all duration-500 ${isAdmin ? 'border-b border-indigo-500/30 shadow-[0_4px_20px_rgba(99,102,241,0.1)]' : 'border-b border-white/5'} ${location.pathname !== '/dashboard' ? 'hidden md:block' : ''}`}>
      <KYCBanner />
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">




        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center group z-50">
            <Link to={isAuthenticated ? (isAdmin ? "/admin" : "/dashboard") : "/"} className="relative flex items-center space-x-3">
              <div className={`absolute -inset-1 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200 ${isAdmin ? 'bg-gradient-to-r from-violet-600 to-indigo-600' : 'bg-gradient-to-r from-indigo-500 to-cyan-500'}`}></div>
              <div className="relative bg-slate-900 border border-white/10 rounded-xl p-2 md:p-2.5 shadow-xl flex items-center gap-2">
                {branding.logo_url ? (
                  <img src={branding.logo_url} alt="Logo" className="h-8" />
                ) : (
                  <span className={`text-xl md:text-2xl font-black bg-clip-text text-transparent tracking-tighter ${isAdmin ? 'bg-gradient-to-r from-violet-400 to-indigo-400' : 'bg-gradient-to-r from-indigo-400 via-cyan-400 to-emerald-400'}`}>
                    {branding.site_name}
                  </span>
                )}
                {isAdmin && (
                  <div className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-500/10 border border-indigo-500/20">
                    <ShieldCheck size={10} className="text-indigo-400" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-indigo-400">HQ</span>
                  </div>
                )}
              </div>
            </Link>
          </div>

          {/* Language Switcher */}
          <div className="hidden lg:flex items-center ml-4 relative lang-dropdown-container">
            <button 
                onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900/50 border border-white/5 text-slate-400 hover:text-white transition-all text-xs font-black uppercase tracking-widest"
            >
                <Globe size={14} className="text-indigo-400" />
                {i18n.language.toUpperCase()}
                <ChevronDown size={12} className={`transition-transform ${isLangDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isLangDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-32 bg-slate-900 border border-white/10 rounded-xl shadow-2xl py-2 z-[60] animate-in fade-in slide-in-from-top-2">
                    <button 
                        onClick={() => { i18n.changeLanguage('fr'); setIsLangDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-xs font-black uppercase tracking-widest hover:bg-white/5 transition-colors ${i18n.language === 'fr' ? 'text-indigo-400' : 'text-slate-400'}`}
                    >
                        Français
                    </button>
                    <button 
                        onClick={() => { i18n.changeLanguage('en'); setIsLangDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-xs font-black uppercase tracking-widest hover:bg-white/5 transition-colors ${i18n.language === 'en' ? 'text-indigo-400' : 'text-slate-400'}`}
                    >
                        English
                    </button>
                    <button 
                        onClick={() => { i18n.changeLanguage('es'); setIsLangDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-xs font-black uppercase tracking-widest hover:bg-white/5 transition-colors ${i18n.language === 'es' ? 'text-indigo-400' : 'text-slate-400'}`}
                    >
                        Español
                    </button>
                </div>
            )}
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-1">
            {!isAuthenticated ? (
              <>
                {publicLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                      location.pathname === link.path 
                        ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 shadow-glow' 
                        : 'text-slate-400 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="flex items-center space-x-3 ml-6">
                  <Link
                    to="/login"
                    className="text-slate-300 hover:text-white px-4 py-2 text-sm font-semibold transition-all"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/register"
                    className="btn-premium text-white shadow-indigo-500/20"
                  >
                    {t('nav.register')}
                  </Link>
                </div>
              </>
            ) : (
              <>
                {/* User specific navigation - hidden for Admin */}
                {!isAdmin && (
                  <div className="flex items-center space-x-1 mr-6 border-r border-white/5 pr-6">
                    {privateLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        className={`px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 ${
                          location.pathname === link.path 
                            ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 shadow-sm' 
                            : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                )}
                {/* Role Specific Links with 'More' Dropdown */}
                {roleLinks.length > 0 && (
                  <div className={`flex items-center space-x-1 ${!isAdmin ? 'border-r border-white/5 mr-6 pr-6' : 'mr-4'}`}>
                    {/* Visible Links (First 3) */}
                    {roleLinks.slice(0, 3).map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-xl text-[11px] font-bold uppercase tracking-tight transition-all duration-300 whitespace-nowrap ${
                          location.pathname === link.path
                            ? (isAdmin 
                                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                                : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 shadow-sm'
                              )
                            : 'text-slate-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <span className="opacity-70">{link.icon}</span>
                        <span>{link.name}</span>
                      </Link>
                    ))}

                    {/* 'More' Dropdown for remaining links */}
                    {roleLinks.length > 3 && (
                      <div className="relative group/more">
                        <button className="flex items-center space-x-1 px-3 py-2 rounded-xl text-[11px] font-bold uppercase tracking-tight text-slate-400 hover:text-white hover:bg-white/5 transition-all">
                          <span>{t('common.more')}</span>
                          <ChevronDown size={14} className="group-hover/more:rotate-180 transition-transform duration-300" />
                        </button>
                        
                        <div className="absolute left-0 mt-2 w-56 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl py-2 z-50 opacity-0 invisible group-hover/more:opacity-100 group-hover/more:visible transition-all duration-300 translate-y-2 group-hover/more:translate-y-0">
                          {roleLinks.slice(3).map((link) => (
                            <Link
                              key={link.name}
                              to={link.path}
                              className={`flex items-center space-x-3 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all ${
                                location.pathname === link.path
                                  ? 'bg-violet-500/10 text-violet-300'
                                  : 'text-slate-400 hover:text-white hover:bg-white/5'
                              }`}
                            >
                              <span className="opacity-70">{link.icon}</span>
                              <span>{link.name}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}



                <div className="flex items-center space-x-2 ml-auto">

                  {/* Support Button */}
                  <Link
                    to="/support"
                    className="p-2 rounded-xl bg-slate-900 border border-white/10 hover:border-emerald-500/30 text-slate-400 hover:text-emerald-400 transition-all relative group"
                    title={t('nav.support') || 'Support'}
                  >
                    <Headphones size={18} className="group-hover:scale-110 transition-transform" />
                  </Link>

                  <div className="relative">
                    <button
                      onClick={() => setIsNotificationDrawerOpen(true)}
                      className="p-2 rounded-xl bg-slate-900 border border-white/10 hover:border-indigo-500/30 text-slate-400 hover:text-white transition-all relative group"
                    >
                      <Bell size={18} className="group-hover:scale-110 transition-transform" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white text-[9px] font-black flex items-center justify-center rounded-full border-2 border-slate-900 shadow-lg">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="relative dropdown-container">

                      <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="group flex items-center space-x-3 bg-slate-900 border border-white/10 hover:border-indigo-500/30 text-white px-4 py-2.5 rounded-2xl text-sm font-semibold transition-all shadow-lg"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center text-xs shadow-inner">
                          {user?.username?.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-slate-200">{getUserDisplayName()}</span>
                        <ChevronDown size={16} className={`text-slate-500 transform transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isDropdownOpen && (
                        <div className="absolute right-0 mt-3 w-56 bg-slate-900 border border-white/10 rounded-2xl shadow-2xl py-2 z-50 animate-fade-in ring-1 ring-black/50">
                          <div className="px-4 py-3 border-b border-white/5 mb-1">
                            <p className="text-xs text-slate-500 uppercase font-bold">{t('admin.account')}</p>
                          </div>
                          {dropdownItems.map((item) => (
                            item.action ? (
                              <button
                                key={item.name}
                                onClick={item.action}
                                className="flex items-center space-x-3 w-full text-left px-4 py-3 text-sm text-slate-300 hover:text-rose-400 hover:bg-rose-500/5 transition-all font-medium"
                              >
                                <span className="text-slate-500 group-hover:text-rose-400 transition-colors">{item.icon}</span>
                                <span>{item.name}</span>
                              </button>
                            ) : (
                              <Link
                                key={item.name}
                                to={item.path}
                                onClick={() => setIsDropdownOpen(false)}
                                className="flex items-center space-x-3 px-4 py-3 text-sm text-slate-300 hover:text-indigo-400 hover:bg-indigo-500/5 transition-all font-medium"
                              >
                                <span className="text-slate-500 group-hover:text-indigo-400 transition-colors">{item.icon}</span>
                                <span>{item.name}</span>
                              </Link>
                            )
                          ))}
                        </div>
                      )}

                    </div>

                </div>
              </>
            )}

          </div>

          {/* Mobile menu button & Notifications */}
          <div className="md:hidden flex items-center space-x-2">
            
            {/* Language Switcher Mobile */}
            <div className="relative lang-dropdown-container">
              <button 
                  onClick={() => setIsLangDropdownOpen(!isLangDropdownOpen)}
                  className="flex items-center justify-center p-2 rounded-xl bg-slate-900 border border-white/10 text-slate-400 hover:text-white transition-all text-[10px] font-black uppercase tracking-widest"
              >
                  <Globe size={18} />
              </button>
              
              {isLangDropdownOpen && (
                  <div className="absolute top-full right-0 mt-2 w-32 bg-slate-900 border border-white/10 rounded-xl shadow-2xl py-2 z-[60] animate-in fade-in slide-in-from-top-2">
                      <button 
                          onClick={() => { i18n.changeLanguage('fr'); setIsLangDropdownOpen(false); }}
                          className={`w-full text-left px-4 py-2 text-xs font-black uppercase tracking-widest hover:bg-white/5 transition-colors ${i18n.language === 'fr' ? 'text-indigo-400' : 'text-slate-400'}`}
                      >
                          Français
                      </button>
                      <button 
                          onClick={() => { i18n.changeLanguage('en'); setIsLangDropdownOpen(false); }}
                          className={`w-full text-left px-4 py-2 text-xs font-black uppercase tracking-widest hover:bg-white/5 transition-colors ${i18n.language === 'en' ? 'text-indigo-400' : 'text-slate-400'}`}
                      >
                          English
                      </button>
                      <button 
                          onClick={() => { i18n.changeLanguage('es'); setIsLangDropdownOpen(false); }}
                          className={`w-full text-left px-4 py-2 text-xs font-black uppercase tracking-widest hover:bg-white/5 transition-colors ${i18n.language === 'es' ? 'text-indigo-400' : 'text-slate-400'}`}
                      >
                          Español
                      </button>
                  </div>
              )}
            </div>

            {isAuthenticated && (
              <>
              <Link
                to="/support"
                className="p-2.5 rounded-xl bg-slate-900 border border-white/10 hover:border-emerald-500/30 text-slate-400 hover:text-emerald-400 transition-all group"
                title={t('nav.support') || 'Support'}
              >
                <Headphones size={20} className="group-hover:scale-110 transition-transform" />
              </Link>
              <button
                onClick={() => setIsNotificationDrawerOpen(true)}
                className="p-2.5 rounded-xl bg-slate-900 border border-white/10 hover:border-indigo-500/30 text-slate-400 hover:text-white transition-all relative group"
              >
                <Bell size={20} className="group-hover:scale-110 transition-transform" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-rose-500 text-white text-[9px] font-black flex items-center justify-center rounded-full border-2 border-slate-900 shadow-lg">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
              </>
            )}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-slate-400 hover:text-white bg-slate-900 border border-white/10 p-2.5 rounded-xl transition-all"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation - only visible on /dashboard */}
        {isMobileMenuOpen && location.pathname === '/dashboard' && (
          <div className="md:hidden pb-6 animate-fade-in">
            {!isAuthenticated ? (
              <div className="space-y-1">
                {publicLinks.map((link) => (
                  <Link
                    key={link.name}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block px-4 py-3 rounded-xl text-base font-semibold ${
                      location.pathname === link.path
                        ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                        : 'text-slate-400 hover:bg-white/5'
                    }`}
                  >
                    {link.name}
                  </Link>
                ))}
                <div className="pt-4 space-y-3">
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full py-3 text-center text-slate-300 font-bold border border-white/10 rounded-xl"
                  >
                    {t('nav.login')}
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="btn-premium block w-full text-center text-white py-3.5"
                  >
                    {t('auth.open_pro_account')}
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-1">
                {/* User specific navigation - hidden for Admin */}
                {!isAdmin && (
                  <>
                    <div className="mb-4">
                      <p className="px-4 text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 mt-2">{t('dashboard.actions.title') || 'Quick Actions'}</p>
                      <div className="grid grid-cols-4 gap-2 px-4 mb-4">
                        {[
                          { title: t('dashboard.actions.deposit') || 'Deposit', action: "/deposit", icon: <ArrowDownRight size={16} /> },
                          { title: t('dashboard.actions.withdraw') || 'Withdraw', action: "/withdraw", icon: <ArrowUpRight size={16} /> },
                          { title: t('dashboard.actions.transfer') || 'Transfer', action: "/transfer", icon: <ArrowRightLeft size={16} /> },
                          { title: "Packs", action: "/packs", icon: <Package size={16} /> }
                        ].map((action, index) => (
                          <Link
                            key={index}
                            to={action.action}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex flex-col items-center justify-center gap-1.5 p-2 bg-slate-900 border border-white/10 rounded-xl hover:border-indigo-500/30 hover:bg-indigo-500/10 text-slate-400 hover:text-indigo-400 transition-all"
                          >
                            {action.icon}
                            <span className="text-[8px] font-bold uppercase tracking-widest text-center truncate w-full">{action.title}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                    {privateLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`block px-4 py-3 rounded-xl text-base font-semibold ${
                          location.pathname === link.path
                            ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/20'
                            : 'text-slate-400 hover:bg-white/5'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </>
                )}
                
                {roleLinks.length > 0 && (
                  <div className={`mt-4 pt-4 ${!isAdmin ? 'border-t border-white/5' : ''} space-y-1`}>
                    {roleLinks.map((link) => (
                      <Link
                        key={link.name}
                        to={link.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className={`flex items-center space-x-3 px-4 py-3 rounded-xl text-base font-semibold ${
                          location.pathname === link.path
                            ? (isAdmin 
                                ? 'bg-violet-500/20 text-violet-300 border border-violet-500/30'
                                : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              )
                            : 'text-slate-400 hover:bg-white/5'
                        }`}
                      >
                        {link.icon}
                        <span>{link.name}</span>
                      </Link>
                    ))}
                  </div>
                )}

                <div className="mt-4 pt-4 border-t border-white/5 space-y-1">
                  {dropdownItems.map((item) => (
                    item.action ? (
                      <button
                        key={item.name}
                        onClick={item.action}
                        className="flex items-center space-x-3 w-full text-left px-4 py-3 text-base font-semibold text-rose-400 hover:bg-rose-500/5 rounded-xl transition-all"
                      >
                        {item.icon}
                        <span>{item.name}</span>
                      </button>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.path}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center space-x-3 px-4 py-3 text-base font-semibold text-slate-300 hover:bg-indigo-500/5 rounded-xl transition-all"
                      >
                        {item.icon}
                        <span>{item.name}</span>
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </nav>
      <NotificationDrawer 
        isOpen={isNotificationDrawerOpen}
        onClose={() => setIsNotificationDrawerOpen(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        markAsRead={markAsRead}
        markAllAsRead={markAllAsRead}
        clearAll={clearAll}
        onNotificationClick={handleNotificationClick}
      />
    </header>

  );
}